import { FieldNames } from '@/constants/FieldNames';
import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import { SourceDataInitials } from '@/constants/SourceDataInitials';
import IComp from '@/models/Comp';
import IFeedData, { newFeedData } from '@/models/comp/IFeedData';
import LocalCompRequest from '@/models/requests/LocalCompRequest';
import HostExchangeService from '@/services/3.6/HostExchangeService';
import LocalCompService from '@/services/3.6/LocalCompService';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import { Actions, Getters, Mutations, store } from '@/store';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import QSUtils from '@/utils/QSUtils';
import utils from '@/utils/QSUtils';
import CompsManager from './CompsManager';

export default {
    /**
     * Fetch a feed data using the given local comp request.
     * @param localCompRequest
     * @returns A feed data which may have no values when not able to retrieve
     */
    async fetchFeedData(request: LocalCompRequest): Promise<IFeedData> {
        const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
        const service = new LocalCompService(webEnvConfig);

        const data = await service.getBestComp(request);
        const payloadSourceData = data?.sourceData ?? data?.compData;
        let feedData = await CompsManager.transformSourceDataPayload(payloadSourceData, SourceDataInitials.Database);
        feedData.isPeerComp = data?.isPeerComp ?? null;
        feedData.peerCount = data?.peerCount ?? null;
        feedData.records = data?.records ?? null;
        feedData.reportUseCount = data?.reportUseCount ?? null;
        feedData.yourCount = data?.yourCount ?? null;

        if (feedData && feedData.sourceData) {
            if (utils.isKeyInObject(feedData.sourceData, FieldNames.CompPhoto)) {
                feedData.sourceData[FieldNames.CompPhoto] = await fetchCompPhoto(feedData);
            }
        }

        return feedData;

    },

    async fetchFeedDataSubComps(feedRequest: LocalCompRequest): Promise<void> {
        await commitSubComps(feedRequest);
    },

    async fetchAndStoreSubComps(): Promise<void> {
        const activeComp = store.getters[Getters.CompAddressesV36.ActiveComp];

        const almCompId = theLocalStorageUtils.get(LocalStorageKeys.almCompId);
        const localCompRequest: LocalCompRequest = {
            compsGuid: almCompId ?? '',
            address: activeComp.address,
        };

        await commitSubComps(localCompRequest);
    },

};

/**
 * Fetch subcomps using the given local comp request.
 * @param request
 * @returns An array of IComp which may be empty when none are retrieved
 */
async function fetchSubComps(request: LocalCompRequest): Promise<IFeedData[]> {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    const service = new LocalCompService(webEnvConfig);

    let feedDatas = await service.getSubComps(request);
    if (!feedDatas) {
        feedDatas = [];
    } else {

        for (const subData of feedDatas) {
            if (subData.sourceData && utils.isKeyInObject(subData.sourceData, FieldNames.CompPhoto)) {
                subData.sourceData[FieldNames.CompPhoto] = await fetchCompPhoto(subData);
            }
        }
    }

    return feedDatas;
}


// /**
//  * Fetch subcomps using the given local comp request.
//  * @param localCompRequest
//  * @returns An array of IComp which may be empty when none are retrieved
//  */
// async function fetchSubComps(localCompRequest: LocalCompRequest): Promise<IFeedData[]> {
//     // Get local versions from Comp store
//     let feedData: IFeedData[] = fetchLocalVersions(localCompRequest);

//     // If no Comp local versions data is found, then check LocalDatabaseComps store
//     if (feedData.length === 0) {
//         const localDatabaseData = store.getters[Getters.CompAddressesV36.LocalDatabaseCompByAddress](localCompRequest.address);
//         if (localDatabaseData && localDatabaseData.localVersions.length > 0) {
//             await store.dispatch(Actions.CompAddressesV36.UpdateLocalVersions, {
//                 qsGuid: localCompRequest.qsGuid,
//                 localVersions: localDatabaseData.localVersions,
//             });

//             feedData = fetchLocalVersions(localDatabaseData);
//         }
//     }

//     if (feedData) {
//         for (const subData of feedData) {
//             if (subData.sourceData && utils.isKeyInObject(subData.sourceData, FieldNames.CompPhoto)) {
//                 subData.sourceData[FieldNames.CompPhoto] = await fetchCompPhoto(subData);
//             }
//         }
//     }

//     return feedData;
// }

// function fetchBestLocalVersion(compRequest: LocalCompRequest): IFeedData {
//     let feedData: IFeedData = {};
//     const localVersions = getLocalVersions(compRequest);
//     if (localVersions.length > 0) {
//         feedData = getLocalVersionFirstComp(localVersions);
//     }

//     return feedData;
// }

// function fetchLocalVersions(compRequest: LocalCompRequest): IFeedData[] {
//     const localVersions: IFeedData[] = getLocalVersions(compRequest);
//     if (localVersions.length > 0) {
//         localVersions.shift();
//     }

//     return localVersions;
// }

// function getLocalVersions(localCompRequest: LocalCompRequest): IFeedData[] {
//     const storeComp: IComp = store.getters[Getters.CompAddressesV36.CompByAddress](localCompRequest.address);
//     const feedDatas: IFeedData[] = [];

//     for (const items of storeComp.localVersions) {
//         const result: IFeedData = newFeedData();
//         const localData = utils.convertAssocArrToKeyValue(items.fieldData, 'fieldName', 'value');
//         localData.COMP_PHOTO = items.propertyPhoto;

//         result.sourceData = localData;
//         result.records = storeComp.localVersions.length;
//         feedDatas.push(result);
//     }

//     return feedDatas;
// }

// function getLocalVersionFirstComp(feedDatas: IFeedData[]): IFeedData {
//     return feedDatas.slice(0, 1).shift() ?? {};
// }

async function fetchCompPhoto(feedData: IFeedData): Promise<string> {
    let compPhoto = '';
    if (feedData.sourceData && utils.isKeyInObject(feedData.sourceData, FieldNames.CompPhoto)) {
        compPhoto = feedData.sourceData[FieldNames.CompPhoto];
        if (compPhoto) {
            const service = HostExchangeService.getInstance();
            compPhoto = await service.getBase64ForImage(compPhoto);
        }
    }

    return compPhoto || '';
}

async function commitSubComps(localCompRequest: LocalCompRequest) {
    const comp = store.getters[Getters.CompAddressesV36.CompByAddress](localCompRequest.address);
    const feedDatas = await fetchSubComps(localCompRequest);

    store.commit(Mutations.CompAddressesV36.ClearSubComps, {
        comp: comp,
        initials: SourceDataInitials.Database,
    });

    if (feedDatas.length > 0) {
        for (const feedData of feedDatas) {
            store.commit(Mutations.CompAddressesV36.AddSubComp, {
                comp: comp,
                initials: SourceDataInitials.Database,
                sourceData: feedData.sourceData,
            });
        }
    }
}
